import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { Search, User, Mail, Unlock, CheckCircle, Calendar, Crown, Shield, Phone, Gift, Loader2, XCircle } from "lucide-react";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [unlockCourse, setUnlockCourse] = useState("");
  
  // Subscription management state
  const [subscriptionMember, setSubscriptionMember] = useState(null);
  const [newSubscriptionType, setNewSubscriptionType] = useState("");
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  
  // Bulk grant state
  const [showBulkGrant, setShowBulkGrant] = useState(false);
  const [bulkEmails, setBulkEmails] = useState("");
  const [bulkGrantLoading, setBulkGrantLoading] = useState(false);
  const [bulkGrantResults, setBulkGrantResults] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      // Fetch members and courses in parallel for faster loading
      const [membersRes, coursesRes] = await Promise.all([
        axios.get(`${API}/admin/members`, {
          withCredentials: true
        }),
        axios.get(`${API}/admin/courses`, {
          withCredentials: true
        }).catch(() => ({ data: [] }))
      ]);
      
      setMembers(membersRes.data);
      
      // Handle both array and object response formats for courses
      const coursesData = coursesRes.data;
      setCourses(Array.isArray(coursesData) ? coursesData : (coursesData.courses || []));
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load members");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/members`, {
        withCredentials: true
      });
      setMembers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Refetch is non-critical - original data remains
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to refetch members:', error);
      }
    }
  };

  const handleUnlock = async () => {
    if (!selectedMember || !unlockCourse) return;
    
    try {
      await axios.post(`${API}/admin/unlock`, {
        user_id: selectedMember.user_id,
        course_id: unlockCourse
      }, {
        withCredentials: true
      });
      toast.success("Course unlocked for member");
      setSelectedMember(null);
      setUnlockCourse("");
      refetchMembers();
    } catch {
      toast.error("Failed to unlock course");
    }
  };

  const handleSubscriptionUpdate = async () => {
    if (!subscriptionMember || !newSubscriptionType) return;
    
    setUpdatingSubscription(true);
    try {
      await axios.put(`${API}/admin/users/${subscriptionMember.user_id}/subscription`, {
        subscription_type: newSubscriptionType
      }, {
        withCredentials: true
      });
      toast.success(`Subscription updated to ${newSubscriptionType}`);
      setSubscriptionMember(null);
      setNewSubscriptionType("");
      refetchMembers();
    } catch {
      toast.error("Failed to update subscription");
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleBulkGrant = async () => {
    const emails = bulkEmails
      .split('\n')
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    
    if (emails.length === 0) {
      toast.error("Please enter at least one valid email address");
      return;
    }
    
    setBulkGrantLoading(true);
    setBulkGrantResults(null);
    
    try {
      const response = await axios.post(`${API}/admin/bulk-grant-annual`, {
        emails: emails
      }, {
        withCredentials: true
      });
      
      setBulkGrantResults(response.data);
      
      if (response.data.summary.granted > 0) {
        toast.success(`Granted annual access to ${response.data.summary.granted} user(s)`);
        refetchMembers();
      }
      
      if (response.data.summary.not_found > 0) {
        toast.warning(`${response.data.summary.not_found} email(s) not found`);
      }
    } catch (error) {
      toast.error("Failed to process bulk grant");
      console.error("Bulk grant error:", error);
    } finally {
      setBulkGrantLoading(false);
    }
  };

  const getSubscriptionBadge = (member) => {
    const type = member.subscription_type || "none";
    const isOverride = member.subscription_override;
    
    const badges = {
      annual: { label: "Annual", color: "bg-[#a69373] !text-white" },
      quarterly: { label: "Quarterly", color: "bg-[#7b9e7b] !text-white" },
      monthly: { label: "Monthly", color: "bg-[#5a7a9a] !text-white" },
      none: { label: "Free", color: "bg-white/20 !text-white" }
    };
    
    const badge = badges[type] || badges.none;
    return (
      <span 
        className={`px-2 py-1 rounded text-xs font-medium ${badge.color} flex items-center gap-1`}
        data-badge-type={type}
      >
        {type === "annual" && <Crown className="w-3 h-3 !text-white" />}
        <span className="!text-white">{badge.label}</span>
        {isOverride && <Shield className="w-3 h-3 ml-1 opacity-60 !text-white" title="Admin override" />}
      </span>
    );
  };

  const formatSubscriptionEnd = (endDate) => {
    if (!endDate) return "N/A";
    const date = new Date(endDate);
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredMembers = members.filter(m => 
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.profile?.first_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-24 bg-muted rounded-xl" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <section className="mb-8 animate-fade-in" data-testid="members-header">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#a69373' }}>Members</h1>
          <p className="text-[#a69373]/70">Manage platform members and their progress.</p>
        </section>

        {/* Search and Bulk Grant */}
        <section className="mb-8 animate-fade-in" style={{ animationDelay: "100ms" }}>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a69373]/40" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search members..."
                className="input-field pl-12"
                data-testid="search-members"
              />
            </div>
            <Button
              onClick={() => {
                setShowBulkGrant(true);
                setBulkGrantResults(null);
                setBulkEmails("");
              }}
              className="bg-[#a69373] hover:bg-[#a69373]/90 text-white"
              data-testid="bulk-grant-btn"
            >
              <Gift className="w-4 h-4 mr-2" />
              Bulk Grant Annual
            </Button>
          </div>
        </section>

        {/* Members List */}
        <section className="space-y-4" data-testid="members-list">
          {filteredMembers.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <p style={{ color: '#a69373' }}>No members found.</p>
              </CardContent>
            </Card>
          ) : (
            filteredMembers.map((member, index) => (
              <Card 
                key={member.user_id}
                className="glass-card"
                data-testid={`member-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                        <User className="w-6 h-6" style={{ color: '#a69373', stroke: '#a69373' }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold" style={{ color: '#a69373' }}>
                            {member.profile?.first_name || member.name || "Anonymous"}
                          </p>
                          {getSubscriptionBadge(member)}
                        </div>
                        <p className="text-sm flex items-center gap-1" style={{ color: 'rgba(166,147,115,0.7)' }}>
                          <Mail className="w-3 h-3" style={{ color: 'rgba(166,147,115,0.7)' }} />
                          {member.email}
                        </p>
                        {member.profile?.phone_number && (
                          <p className="text-sm flex items-center gap-1" style={{ color: 'rgba(166,147,115,0.7)' }}>
                            <Phone className="w-3 h-3" style={{ color: 'rgba(166,147,115,0.7)' }} />
                            {member.profile.phone_country_code || "+27"}{member.profile.phone_number}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-1 text-sm" style={{ color: 'rgba(166,147,115,0.6)' }}>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" style={{ color: 'rgba(166,147,115,0.6)' }} />
                            {member.lessons_completed} lessons
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" style={{ color: 'rgba(166,147,115,0.6)' }} />
                            {new Date(member.created_at).toLocaleDateString()}
                          </span>
                          {member.subscription_end && (
                            <span className="flex items-center gap-1" style={{ color: '#a69373' }}>
                              Expires: {formatSubscriptionEnd(member.subscription_end)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        {member.progress?.map((p) => (
                          <span 
                            key={p.course_id}
                            className={`px-2 py-1 rounded text-xs ${
                              p.completed 
                                ? "bg-primary/20 text-primary" 
                                : p.unlocked 
                                  ? "bg-secondary text-secondary-foreground"
                                  : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {p.course_id?.replace("course_", "").replace("wakeup", "WU").replace("burnmap", "BM").replace("whole", "W")}
                          </span>
                        ))}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#a69373]/30 hover:bg-[#a69373]/10"
                        onClick={() => {
                          setSubscriptionMember(member);
                          setNewSubscriptionType(member.subscription_type || "none");
                        }}
                        data-testid={`manage-subscription-${index}`}
                        style={{ color: '#a69373' }}
                      >
                        <Crown className="w-4 h-4 mr-1" style={{ color: '#a69373', stroke: '#a69373' }} />
                        Tier
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#a69373]/30 hover:bg-[#a69373]/10"
                        onClick={() => setSelectedMember(member)}
                        style={{ color: '#a69373' }}
                      >
                        <Unlock className="w-4 h-4 mr-1" style={{ color: '#a69373', stroke: '#a69373' }} />
                        Unlock
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </section>

        {/* Unlock Dialog */}
        <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Unlock Course for {selectedMember?.profile?.first_name || selectedMember?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4 overflow-x-hidden">
              <Select value={unlockCourse} onValueChange={setUnlockCourse}>
                <SelectTrigger className="bg-input border-white/10 text-foreground w-full">
                  <SelectValue placeholder="Select course to unlock" />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10 max-w-[calc(100vw-4rem)]">
                  {(courses || []).map((course) => (
                    <SelectItem key={course.course_id} value={course.course_id}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setSelectedMember(null)}>
                  Cancel
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={handleUnlock}
                  disabled={!unlockCourse}
                >
                  Unlock Course
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Subscription Management Dialog */}
        <Dialog open={!!subscriptionMember} onOpenChange={() => setSubscriptionMember(null)}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#a69373]" />
                Manage Subscription
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="text-sm text-muted-foreground">Member</p>
                <p className="text-foreground font-medium">
                  {subscriptionMember?.profile?.first_name || subscriptionMember?.name || "Anonymous"}
                </p>
                <p className="text-sm text-muted-foreground">{subscriptionMember?.email}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-foreground">Subscription Tier</Label>
                <Select value={newSubscriptionType} onValueChange={setNewSubscriptionType}>
                  <SelectTrigger className="bg-input border-white/10 text-foreground" data-testid="subscription-tier-select">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="none">
                      <span className="flex items-center gap-2">Free (No Access)</span>
                    </SelectItem>
                    <SelectItem value="monthly">
                      <span className="flex items-center gap-2">Monthly (R189 - 30 days)</span>
                    </SelectItem>
                    <SelectItem value="quarterly">
                      <span className="flex items-center gap-2">Quarterly (R479 - 90 days)</span>
                    </SelectItem>
                    <SelectItem value="annual">
                      <span className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-[#a69373]" />
                        Annual (R1590 - 365 days)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 rounded-lg bg-[#a69373]/10 border border-[#a69373]/20">
                <p className="text-xs text-[#a69373] flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  This will mark the account as "Admin Override" for testing purposes.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setSubscriptionMember(null)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821]"
                  onClick={handleSubscriptionUpdate}
                  disabled={!newSubscriptionType || updatingSubscription}
                  data-testid="update-subscription-btn"
                >
                  {updatingSubscription ? "Updating..." : "Update Subscription"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Grant Annual Dialog */}
        <Dialog open={showBulkGrant} onOpenChange={setShowBulkGrant}>
          <DialogContent className="bg-[#0B2821] border border-white/20 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2" style={{ color: '#a69373' }}>
                <Gift className="w-5 h-5" />
                Bulk Grant Annual Access
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg bg-amber-900/20 border border-amber-500/20">
                <p className="text-sm text-amber-200">
                  <strong>Note:</strong> Only existing registered users will be granted access. 
                  Unregistered emails will be reported as "not found" — no empty accounts are created.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#a69373]">Email Addresses (one per line)</Label>
                <Textarea
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                  className="min-h-[150px] bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="bulk-emails-input"
                />
                <p className="text-xs text-white/50">
                  {bulkEmails.split('\n').filter(e => e.trim() && e.includes('@')).length} valid email(s) entered
                </p>
              </div>
              
              {/* Results */}
              {bulkGrantResults && (
                <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Granted: {bulkGrantResults.summary.granted}
                    </span>
                    <span className="flex items-center gap-1 text-red-400">
                      <XCircle className="w-4 h-4" />
                      Not Found: {bulkGrantResults.summary.not_found}
                    </span>
                  </div>
                  
                  {/* Detailed results */}
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {bulkGrantResults.results.map((r, idx) => (
                      <div 
                        key={idx} 
                        className={`text-xs p-2 rounded flex items-center gap-2 ${
                          r.status === 'granted' 
                            ? 'bg-green-900/20 text-green-300' 
                            : 'bg-red-900/20 text-red-300'
                        }`}
                      >
                        {r.status === 'granted' ? (
                          <CheckCircle className="w-3 h-3 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-3 h-3 flex-shrink-0" />
                        )}
                        <span className="truncate">{r.email}</span>
                        <span className="ml-auto text-white/50">{r.status === 'granted' ? 'Granted' : 'Not found'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-2">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowBulkGrant(false)}
                  className="text-white hover:bg-white/10"
                >
                  {bulkGrantResults ? "Close" : "Cancel"}
                </Button>
                {!bulkGrantResults && (
                  <Button 
                    className="bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821]"
                    onClick={handleBulkGrant}
                    disabled={bulkGrantLoading || !bulkEmails.trim()}
                    data-testid="submit-bulk-grant-btn"
                  >
                    {bulkGrantLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        Grant Annual Access
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
